import {
  AttachMoney,
  Circle,
  Delete,
  FilterList,
  Home,
  ImportExport,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material'
import {
  Badge,
  Box,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Holidays from 'date-holidays'
import dayjs from 'dayjs'
import { t } from 'i18next'
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { defaultPageSize } from '../../../config/defaultPagination'
import { useNotification } from '../../../store/redux/hooks'
import { getCurrentUser } from '../../../store/redux/reducers/currentUserSlice'
import { getWorkHourCollapseThreshold } from '../../../store/redux/reducers/settingsSlice'
import { getSidebarState } from '../../../store/redux/reducers/sidebarSlice'
import { useDeleteUserWorklogEntriesMutation } from '../../../store/redux/reducers/worklogSlice'
import { Column, Day, WorkDay, WorkEntry } from '../../../store/types'
import DateFormat from '../../../store/types/enums/DateFormat'
import LocalStorageTypes from '../../../store/types/enums/LocalStorageTypes'
import { sumAllPerDay, sumProjectPerDay } from '../../../store/utils'
import { getHolidayNamesString } from '../../../store/utils/holidaysUtils'
import { isRemote, roundUpToNearestMinute } from '../../../store/utils/worklogUtils'
import { SquareToggleButton } from '../../../styles/ButtonStyles'
import {
  StyledContainerColumnLeft,
  StyledContainerRowBetween,
  StyledContainerRowEnd,
  StyledContainerRowStart,
} from '../../../styles/ContainerStyles'
import { StyledBoxInStickyCell, StyledWorklogTableRow } from '../../../styles/WorklogTableStyles'
import ConfirmationDialog from '../../Dialog/ConfirmationDialog'
import FilterEntriesMenu from '../../FilterMenu/Worklog/FilterEntriesMenu'
import DecimalHourDurationTooltip from '../../Tooltip/DecimalHourDurationTooltip'
import AddEntryForm from './Entry/AddEntryForm'
import EntryTable from './Entry/EntryTable'
import WorklogColumnCell from './WorklogColumnCell'
import { ColumnLayout } from './WorklogTable'

type WorklogTableRowProps = {
  day: Day
  columns: Column[]
  worklog: WorkDay[] | undefined
  vacationId: string
  displayVacation: boolean
  isReadOnly: boolean
  setIsChangePresent: React.Dispatch<React.SetStateAction<boolean[]>>
  columnLayout: ColumnLayout
}

const WorklogTableRow = forwardRef<
  {
    closeRow: () => void
  },
  WorklogTableRowProps
>(({ day, columns, worklog, vacationId, displayVacation, isReadOnly, setIsChangePresent, columnLayout }, ref) => {
  const { displayNotification } = useNotification()

  const [isRowCollapsed, setIsRowCollapsed] = useState(false)

  useImperativeHandle(ref, () => ({
    closeRow: () => setIsRowCollapsed(false),
  }))

  const sidebarState = useSelector(getSidebarState)

  const entries: WorkEntry[] = useMemo(() => {
    return worklog?.filter(d => d.date === day.date.format(DateFormat.YearMonthDay))[0]?.entries || []
  }, [worklog, day.date])

  const threshold = useSelector(getWorkHourCollapseThreshold)
  const [entryAddedFlag, setEntryAddedFlag] = useState<boolean>(false)

  useEffect(() => {
    if (threshold === 0) {
      return
    }
    if (entries.length >= threshold && entryAddedFlag) {
      setIsRowCollapsed(false)
      setEntryAddedFlag(false)
    }
  }, [entries.length, threshold, entryAddedFlag])

  const todaysTotal = roundUpToNearestMinute(sumAllPerDay(worklog, day.date.format(DateFormat.YearMonthDay)))
  const vacationTotal = sumProjectPerDay(
    worklog,
    vacationId,
    day.date.format(DateFormat.YearMonthDay),
    day.date.format(DateFormat.YearMonthDay)
  )
  const isVacationPaid = worklog
    ?.find(w => w.date === day.date.format(DateFormat.YearMonthDay))
    ?.entries.some(e => e.paidVacation)

  const projectIds = [vacationId, ...columns.map(value => value.projectId)].flat()

  const hd = new Holidays('PL', '', '', { types: ['public'] })

  const holiday = hd.isHoliday(day.date.toDate())

  const getTableRowHeaderTitle = (date: dayjs.Dayjs) => {
    let title = `${date.format(DateFormat.DayName)}, ${date.format(DateFormat.DayMonthNameYear)} `
    title = title.charAt(0).toUpperCase() + title.slice(1)

    return title
  }

  const [openFilterMenu, setOpenFilterMenu] = useState(false)
  const [filteringProps, setFilteringProps] = useState<{
    projects: string[]
    timeFrom: number
    timeTo: number
  }>({
    projects: [...new Set(entries.map(e => e.project.id))],
    timeFrom: 0,
    timeTo: 24,
  })

  useEffect(() => {
    setFilteringProps({
      projects: [...new Set(entries.map(e => e.project.id))],
      timeFrom: 0,
      timeTo: 24,
    })
  }, [day.date, entries, worklog])

  const checkDate = day.date.toDate().getMonth()

  useEffect(() => {
    setIsRowCollapsed(false)
  }, [checkDate])

  useEffect(() => {
    if (!isRowCollapsed) {
      setOpenFilterMenu(false)
      setSelected([])
      setEditMode(false)
      setEditedEntry(null)
      return
    }

    setFilteringProps({
      projects: [...new Set(entries?.map(e => e.project.id))],
      timeFrom: 0,
      timeTo: 24,
    })
  }, [day.date, entries, isRowCollapsed, worklog])

  const [rowsPerPage, setRowsPerPage] = useState<number>(
    parseInt(localStorage.getItem(LocalStorageTypes.EntryTableRows) || defaultPageSize.toString())
  )

  const [editMode, setEditMode] = useState(false)
  const [editedEntry, setEditedEntry] = useState<WorkEntry | null>(null)

  const theme = useTheme()
  const isLg = useMediaQuery(theme.breakpoints.down('lg'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  const getRowStyles = () => {
    if (holiday && !day.isWeekDay) return 'holiday-weekend'
    if (holiday) return 'holiday'
    if (!day.isWeekDay) return ' weekend'
    return ''
  }

  const [deleteEntries] = useDeleteUserWorklogEntriesMutation()
  const currentUser = useSelector(getCurrentUser)
  const id = currentUser?.id === undefined ? '' : currentUser?.id
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)

  const handleDelete = async () => {
    if (id === undefined) {
      displayNotification({ message: t('page.worklog.alert.deleteEntriesError'), type: 'error' })
      return
    }
    try {
      await deleteEntries({ employeeId: id, workHoursIds: selected.map(value => value.id) }).unwrap()
      displayNotification({ message: t('page.worklog.alert.deleteEntriesSuccess'), type: 'success' })
      if (editedEntry !== null && selected.some(value => value.id === editedEntry.id)) {
        setEditMode(false)
        setEditedEntry(null)
      }
    } catch (_) {
      displayNotification({ message: t('page.worklog.alert.deleteEntriesError'), type: 'error' })
    }

    setOpenDeleteDialog(false)
    setSelected([])
  }

  const [selected, setSelected] = useState<WorkEntry[]>([])

  const wouldBreakHoursRule = useMemo(() => {
    if (selected.length === 0) return false
    const selectedIds = new Set(selected.map(s => s.id))
    const remainingEntries = entries.filter(e => !selectedIds.has(e.id))

    const hoursByProject = new Map<string, number>()
    for (const entry of remainingEntries) {
      const current = hoursByProject.get(entry.project.id) ?? 0
      hoursByProject.set(entry.project.id, current + entry.hours)
    }

    for (const totalHours of hoursByProject.values()) {
      if (totalHours > 0 && totalHours % 0.5 !== 0) {
        return true
      }
    }
    return false
  }, [selected, entries])

  const { col1Width, col2Width, col3Width, paddingLeft: multiColumnPaddingLeft } = columnLayout

  return (
    <React.Fragment>
      <StyledWorklogTableRow
        onClick={() => setIsRowCollapsed(!isRowCollapsed)}
        className={getRowStyles()}
        selected={isRowCollapsed}
        sx={{
          height: '50px',
          transition: 'all 150ms ease-in-out',
          ':hover': {
            cursor: 'pointer',
          },
        }}
      >
        <TableCell
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 31 + 1 - day.id,
            p: 0,
          }}
          colSpan={3}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: 'fit-content',
              pl: `${multiColumnPaddingLeft}px`,
            }}
          >
            <Box sx={{ width: col1Width }}>
              <IconButton
                size="small"
                onClick={() => setIsRowCollapsed(!isRowCollapsed)}
                sx={{
                  color: 'text.base',
                }}
                aria-label={isRowCollapsed ? t('page.worklog.table.collapse') : t('page.worklog.table.expand')}
              >
                {isRowCollapsed ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            </Box>

            <StyledBoxInStickyCell sx={{ width: col2Width }}>
              <Typography sx={{ width: '50px' }} variant="body2">
                {day.date.format('DD ddd')}
              </Typography>
              {isRemote(entries) && <Home sx={{ color: theme.palette.primary.main, width: 18, height: 18, pb: 0.2 }} />}
            </StyledBoxInStickyCell>

            <StyledBoxInStickyCell
              sx={{
                justifyContent: 'center',
                width: col3Width,
              }}
            >
              {todaysTotal === 0 ? (
                '-'
              ) : (
                <DecimalHourDurationTooltip hours={todaysTotal} arrow>
                  <span>{todaysTotal}</span>
                </DecimalHourDurationTooltip>
              )}
            </StyledBoxInStickyCell>
          </Box>
        </TableCell>

        {displayVacation && (
          <TableCell
            sx={{
              width: '55px',
              borderRight: '1px solid',
              borderRightColor: 'background.elevation8',
              paddingX: '16px',
            }}
            align="center"
          >
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              badgeContent={<>{isVacationPaid ? <AttachMoney sx={{ width: 12, height: 12, ml: 1.5 }} /> : ''}</>}
            >
              {vacationTotal === 0 ? '-' : vacationTotal}
            </Badge>
          </TableCell>
        )}

        {columns?.length === 0 ? (
          <TableCell align="center">-</TableCell>
        ) : (
          columns.map(column => (
            <WorklogColumnCell
              key={`${column.id}-${column.label}-${column.projectId}`}
              column={column}
              worklog={worklog}
              date={day.date}
            />
          ))
        )}
      </StyledWorklogTableRow>

      <TableRow>
        <TableCell
          sx={{
            paddingBottom: 0,
            paddingTop: 0,
            border: '0px',
            borderBottom: isRowCollapsed ? '1px solid' : '0px',
            borderColor: 'background.elevation8',
            '&.weekend': {
              borderColor: 'red',
            },
            paddingLeft: 0,
            paddingRight: 0,
            width: {
              xs: 'calc(100vw - 165px)',
              sm: `calc(100vw - ${sidebarState ? '325' : '200'}px)`,
              md: openFilterMenu
                ? `calc(100vw - ${sidebarState ? '350' : '200'}px + 225px)`
                : `calc(100vw - ${sidebarState ? '350' : '200'}px)`,
            },
          }}
          colSpan={columns.length + (displayVacation ? 4 : 3) + (columns.length === 0 ? 1 : 0)}
        >
          <Collapse in={isRowCollapsed} timeout="auto" unmountOnExit>
            <StyledContainerColumnLeft
              sx={{
                px: { xs: 2, md: 4 },
                pt: { xs: 2, sm: 3, md: 4 },
                gap: { xs: 0, md: 2 },
                position: 'sticky',
                left: 0,
                width: {
                  xs: 'calc(100vw - 121px)',
                  sm: `calc(100vw - ${sidebarState ? '334' : '153'}px)`,
                  md: openFilterMenu ? `auto` : `calc(100vw - ${sidebarState ? '350' : '169'}px)`,
                },
              }}
            >
              <StyledContainerRowBetween>
                <StyledContainerColumnLeft>
                  <Typography variant="h5" component="h2" color="text.highEmphasis">
                    {getTableRowHeaderTitle(day.date)}
                  </Typography>
                  {holiday && (
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'accent.orange.text' }}>
                      {getHolidayNamesString(holiday)}
                    </Typography>
                  )}
                </StyledContainerColumnLeft>
              </StyledContainerRowBetween>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  gap: 2,
                  height: { xs: '100%', lg: '100%' },
                  pb: openFilterMenu ? 2 : 0,
                }}
              >
                {!isReadOnly && (
                  <Box
                    sx={{
                      height: { xs: '100%', xl: '100%' },
                      width: '100%',
                      flexGrow: 1,
                      flexBasis: 0,
                      order: 1,
                    }}
                  >
                    <AddEntryForm
                      day={day}
                      editMode={editMode}
                      setEditMode={setEditMode}
                      editedEntry={editedEntry}
                      setEditedEntry={setEditedEntry}
                      openFilterMenu={openFilterMenu}
                      projectIds={projectIds}
                      setIsChangePresent={setIsChangePresent}
                      setEntryAddedFlag={setEntryAddedFlag}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    height: { xs: 'fit-content', xl: '100%' },
                    flexGrow: 1,
                    flexBasis: 0,
                    order: isLg ? 3 : 2,
                  }}
                >
                  <StyledContainerColumnLeft sx={{ flex: 1, height: '100%', gap: 1.5 }}>
                    <StyledContainerRowBetween>
                      <StyledContainerRowStart
                        sx={{ gap: !isDownSm ? 4 : 0, flexDirection: !isDownSm ? 'row' : 'column' }}
                      >
                        <Typography variant="h6" component="h3">
                          {t('page.worklog.table.registered')}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: !isDownSm ? 'center' : 'flex-start',
                            gap: 1,
                          }}
                        >
                          <Circle sx={{ width: 14, height: 14, color: 'background.elevation5' }} />
                          <Typography variant="caption" sx={{ pt: 0.25 }}>
                            {t('components.popup.info.worklog.remote')}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: !isDownSm ? 'center' : 'flex-start',
                            gap: 1,
                          }}
                        >
                          <ImportExport sx={{ color: theme.palette.info.main, fontSize: 16, mr: 0.5 }} />
                          <Typography variant="caption" sx={{ pt: 0.25 }}>
                            {t('components.popup.info.worklog.fromImport')}
                          </Typography>
                        </Box>
                      </StyledContainerRowStart>

                      <StyledContainerRowEnd sx={{ gap: { xs: 1, md: 2 } }}>
                        {!isReadOnly && (
                          <Tooltip
                            disableHoverListener={
                              (selected === undefined || selected.length === 0) && !wouldBreakHoursRule
                            }
                            title={
                              wouldBreakHoursRule
                                ? t('page.worklog.table.deleteBreaksHoursRule')
                                : t('page.worklog.table.deleteSelected')
                            }
                          >
                            <span>
                              <SquareToggleButton
                                value="value"
                                color="secondary"
                                size="small"
                                disabled={selected === undefined || selected.length === 0 || wouldBreakHoursRule}
                                onClick={() => setOpenDeleteDialog(true)}
                              >
                                <Delete />
                              </SquareToggleButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip
                          title={t('page.worklog.table.filter')}
                          onClick={() => setOpenFilterMenu(!openFilterMenu)}
                        >
                          <SquareToggleButton value="value" selected={openFilterMenu} color="secondary" size="small">
                            <FilterList />
                          </SquareToggleButton>
                        </Tooltip>
                      </StyledContainerRowEnd>
                    </StyledContainerRowBetween>

                    <EntryTable
                      worklog={worklog}
                      day={day}
                      rowsPerPage={rowsPerPage}
                      setRowsPerPage={setRowsPerPage}
                      filteringProps={filteringProps}
                      editMode={editMode}
                      setEditMode={setEditMode}
                      setEditedEntry={setEditedEntry}
                      todaysTotal={todaysTotal}
                      editedEntry={editedEntry}
                      isFilterMenuOpen={openFilterMenu}
                      isReadOnly={isReadOnly}
                      selected={selected}
                      setSelected={setSelected}
                    />
                  </StyledContainerColumnLeft>
                </Box>
                <Collapse
                  in={openFilterMenu}
                  orientation={isLg ? 'vertical' : 'horizontal'}
                  sx={{ order: isLg ? 2 : 3 }}
                  style={{ width: isLg ? '100%' : '' }}
                  unmountOnExit
                >
                  <FilterEntriesMenu entries={entries ?? []} setFilteringProps={setFilteringProps} />
                </Collapse>
              </Box>
            </StyledContainerColumnLeft>
          </Collapse>
        </TableCell>
      </TableRow>

      <ConfirmationDialog
        open={openDeleteDialog}
        setOpen={setOpenDeleteDialog}
        handleConfirm={handleDelete}
        title={t('page.worklog.dialog.multipleDelete.title')}
        content={t('page.worklog.dialog.multipleDelete.content')}
      />
    </React.Fragment>
  )
})

export default React.memo(WorklogTableRow)
